/**
 * Tooltip Content Constants for Onboarding
 * Provides helpful explanations for form fields and questions that might be unclear
 */

export interface TooltipContent {
  title?: string;
  content: string;
}

export const ONBOARDING_TOOLTIPS: Record<string, TooltipContent> = {
  // STEP 1 - Contact Info
  'voting-parcels': {
    title: 'What are Voting Parcels?',
    content:
      "This is the exact number of properties that have voting rights in your community.\n\nFor most HOAs, this equals the number of homes or units. However, some communities may have:\n• Non-voting parcels (like common areas)\n• Commercial properties without voting rights\n• Vacant lots that don't count toward voting\n\nOnly count properties with actual voting rights in community decisions.",
  },
  'community-type-hoa': {
    title: 'Homeowners Association (HOA)',
    content:
      'A community of single-family homes where residents own their individual lot and home, plus shared ownership of common areas.\n\nTypical features:\n• Individual home ownership\n• Shared amenities (parks, pools, clubhouses)\n• Elected board governance\n• Monthly or annual dues\n• CC&Rs (rules and regulations)',
  },
  'community-type-condo': {
    title: 'Condominium Association',
    content:
      'Residents own individual units within buildings, plus shared ownership of common areas and building structures.\n\nKey characteristics:\n• Individual unit ownership\n• Shared building ownership\n• Association maintains exteriors\n• Shared facilities and amenities\n• Higher density living',
  },
  'community-type-landowner': {
    title: 'Land Owner Association',
    content:
      'A community where residents own large lots or parcels of land, typically with fewer shared amenities.\n\nCommon features:\n• Larger individual properties\n• Rural or semi-rural settings\n• Fewer shared amenities\n• Focus on property maintenance standards\n• Lower monthly dues',
  },
  'community-type-master': {
    title: 'Master-Planned Community',
    content:
      'A large development with multiple neighborhoods, extensive amenities, and comprehensive planning.\n\nTypical features:\n• Multiple sub-neighborhoods\n• Extensive amenities (golf, shopping, schools)\n• Master association governance\n• Sub-associations for neighborhoods\n• Long-term development plans',
  },
  'community-type-mixed': {
    title: 'Mixed-Use Development',
    content:
      'A community combining residential units with commercial spaces, all managed under one association.\n\nKey elements:\n• Residential and commercial properties\n• Shops, offices, or restaurants on-site\n• Integrated community planning\n• Complex governance structure\n• Diverse revenue streams',
  },

  // STEP 2 - Financial Assessment
  'annual-budget': {
    title: 'Annual Community Budget',
    content:
      "This is your community's total annual operating budget - all income and expenses for the year.\n\nWhat to include:\n• Monthly HOA/condo fees (×12)\n• Special assessments\n• Reserve fund contributions\n• Operating expenses (maintenance, utilities, insurance)\n• Administrative costs\n• Capital improvements\n\nWhy we ask: This helps us understand your community size, complexity, and the scale of financial management needed.\n\nExample: 100 units × $200/month = $240,000 + reserves/assessments",
  },
  'monthly-hoa-fee': {
    title: 'Monthly HOA/Association Fee',
    content:
      "The regular monthly fee each unit owner pays to the association.\n\nWhat to include:\n• Base monthly assessment\n• Regular maintenance fees\n• Common area upkeep costs\n\nDon't include:\n• Special assessments\n• One-time fees\n• Capital improvement charges\n\nWhy we ask: This helps calculate your annual revenue and determine the right financial management tools for your community size.\n\nTip: If fees vary by unit type, use the most common amount.",
  },
  'reserve-funds': {
    title: 'Emergency & Capital Reserve Funds',
    content:
      'Money set aside for major repairs, replacements, and unexpected expenses.\n\nTypical reserves include:\n• Building repairs (roofs, siding, HVAC)\n• Infrastructure (roads, parking, utilities)\n• Amenity maintenance (pools, clubhouses)\n• Equipment replacement\n• Emergency fund (typically 3-6 months operating expenses)\n\nWhy we ask: Reserve levels indicate financial health and help us recommend appropriate treasury management features.\n\nIndustry standard: 10-30% of annual budget in reserves',
  },
  'outstanding-debts': {
    title: 'Outstanding Debts & Loans',
    content:
      'Any money your association currently owes to lenders or creditors.\n\nCommon types:\n• Construction/improvement loans\n• Equipment financing\n• Special assessment loans\n• Legal settlement payments\n• Contractor payment agreements\n\nWhy we ask: Debt management affects cash flow and may require specific accounting features or payment tracking tools.\n\nInclude: Principal balance remaining on any loans or structured payment agreements',
  },

  // STEP 3 - Governance
  'governance-experience': {
    title: 'Community Governance Experience',
    content:
      'This includes any experience with HOA/community decision-making such as: serving on a board, participating in committees, attending meetings regularly, or leading community initiatives.',
  },
  'governance-interests': {
    title: 'Areas of Governance Interest',
    content:
      'These are different ways your community can be organized and managed. Understanding your current structure helps us recommend the right tools.\n\nStructure types:\n• HOA Board Only: Elected residents handle everything\n• HOA Board + Management Company: Board sets policy, professionals handle operations\n• Self-Managed: Residents do all management tasks themselves\n• Property Management Led: Professional company handles most decisions\n• Mixed Governance: Different approaches for different community areas\n\nWhy this matters: Different structures need different software features and workflows.',
  },
  'governance-hoa-board-only': {
    title: 'HOA Board Only',
    content:
      'The community is entirely self-governed by elected homeowner board members with no professional management company involved in day-to-day operations.',
  },
  'governance-hoa-management': {
    title: 'HOA Board + Management Company',
    content:
      'Elected homeowner board makes policy decisions, while a professional management company handles daily operations like maintenance coordination and administrative tasks.',
  },
  'governance-mixed-structure': {
    title: 'Mixed Governance Structure',
    content:
      'A combination of different governance approaches, such as some areas managed by the board, others by a management company, or shared governance with municipal authorities.',
  },
  'time-commitment': {
    title: 'Time Commitment Guide',
    content:
      '**1-2 hours:** Basic voting and monthly updates\n\n**3-5 hours:** Regular meetings and discussions\n\n**6-10 hours:** Active committee work and projects\n\n**10+ hours:** Leadership roles and major initiatives\n\n*This helps us recommend the right tools for your involvement level.*',
  },

  // STEP 3 - Current Software
  'basic-hoa-software': {
    title: 'Basic HOA Software',
    content:
      'Simple, entry-level platforms that typically handle basic functions like payment collection, document storage, and simple communication. Examples: Buildium Basic, AppFolio Essentials.',
  },
  'multiple-point-solutions': {
    title: 'Multiple Point Solutions',
    content:
      'Using several different software tools for different functions - one for accounting, another for communication, another for maintenance requests, etc. Often requires manual data transfer between systems.',
  },
  'current-budget-ranges': {
    title: 'Current Software Budget Guide',
    content:
      'Understanding your current spending helps us recommend the right solution.\n\n**Realistic budget breakdown:**\n• $0: No paid software, using free tools only\n• $1-200: Basic tools like QuickBooks, Google Workspace, simple websites\n• $201-500: Entry-level HOA software (Buildium Basic, TOPS Essential)\n• $501-1,000: Professional management software with multiple features\n• $1,001-2,500: Comprehensive platforms (full Buildium, AppFolio, Yardi)\n• $2,500+: Enterprise solutions with custom integrations\n\nThis helps us understand your investment level and current pain points.',
  },

  // STEP 4 - Feature Preferences
  'integrated-financial-management': {
    title: 'Integrated Financial Management',
    content:
      'All your financial functions in one comprehensive system, eliminating the need for multiple tools.\n\nCore features included:\n• Budgeting and forecasting\n• Accounts receivable and payable\n• Financial reporting and analytics\n• Reserve studies and planning\n• Audit preparation and compliance\n• Bank reconciliation\n\nBenefits: No more switching between QuickBooks, Excel, and other tools. Everything syncs automatically.',
  },
  'multi-community-management': {
    title: 'Multi-Community Management',
    content:
      'Tools for managing multiple properties or communities from one dashboard. Useful for management companies or board members involved with several communities.',
  },
  'municipal-integration': {
    title: 'Municipal Integration',
    content:
      'Direct connections with city/county systems for permits, utilities, tax information, and regulatory compliance. Streamlines interaction with local government.',
  },
  'analytics-insights': {
    title: 'Analytics & Insights',
    content:
      'Data dashboards showing trends in spending, resident satisfaction, maintenance patterns, and financial performance. Helps make data-driven decisions about community management.',
  },
  'advanced-communication-hub': {
    title: 'Advanced Communication Hub',
    content:
      'Centralized platform for all community communications with intelligent delivery and tracking.\n\nKey capabilities:\n• Community-wide announcements\n• Discussion forums by topic\n• Direct messaging between residents\n• Emergency alert system\n• Automated notifications\n• Delivery confirmation and read receipts\n\nResult: No more missed communications or residents saying "I never got that message."',
  },
  'smart-maintenance-scheduling': {
    title: 'Smart Maintenance Scheduling',
    content:
      'AI-powered scheduling that optimizes maintenance tasks, predicts equipment failures, and automatically coordinates with vendors based on historical data and patterns.',
  },
  'community-marketplace': {
    title: 'Community Marketplace',
    content:
      'Internal marketplace where residents can buy/sell items, recommend services, share resources, and participate in group purchasing opportunities.',
  },
  'digital-governance-tools': {
    title: 'Digital Governance Tools',
    content:
      'Electronic voting, digital meeting management, online document approval workflows, and automated compliance tracking for board decisions and community governance.',
  },
  'global-network-access': {
    title: 'Global Network Access',
    content:
      'Connect with other communities worldwide to share best practices, negotiate better vendor rates through collective purchasing power, and access expanded service networks.',
  },

  // STEP 5 - Goals & Objectives
  'operational-efficiency': {
    title: 'Operational Efficiency',
    content:
      'Streamlining daily tasks to reduce time spent on administrative work. Examples: automated payment processing, digital document workflows, integrated vendor communication.',
  },
  'competitive-advantage': {
    title: 'Create Competitive Advantage',
    content:
      'Using technology and processes that make your community more attractive to buyers/renters and more efficient than similar communities. This can increase property values and resident satisfaction.',
  },
  'time-savings-metric': {
    title: 'Time Savings Measurement',
    content:
      'Tracking reduction in hours spent on tasks like meeting preparation, financial reporting, vendor coordination, and responding to resident requests.',
  },
  'cost-reduction-metric': {
    title: 'Cost Reduction Measurement',
    content:
      'Measuring savings from group buying, more efficient vendor management, reduced professional service fees, and automated processes that eliminate manual work.',
  },
  'regulatory-compliance-goal': {
    title: 'Ensure Regulatory Compliance',
    content:
      'Meeting all HOA legal requirements, state regulations, financial reporting standards, and maintaining proper documentation for audits and legal protection.',
  },
  'modernize-operations': {
    title: 'Modernize Operations',
    content:
      'Transitioning from manual, paper-based processes to digital workflows, cloud-based access, and modern communication tools to improve efficiency and resident experience.',
  },

  // STEP 6 - Budget & Timeline
  'decision-committee': {
    title: 'Committee Decision',
    content:
      'A formal committee (like Finance, Architecture, or Planning Committee) needs to review and approve the decision, typically involving 3-7 people with specific expertise.',
  },
  'decision-multiple-stakeholders': {
    title: 'Multiple Stakeholders',
    content:
      'Various parties need input - board members, management company, key residents, legal counsel, or accountants. Less formal than a committee but involves several people.',
  },
  'budget-flexibility': {
    title: 'Understanding Budget Flexibility',
    content:
      'This helps us understand how to present pricing and value propositions.\n\nFlexibility levels:\n• Very rigid: Must stay within initial budget range\n• Somewhat flexible: Can increase budget up to 25% for clear value\n• Flexible: Willing to pay more if ROI is demonstrated\n• Very flexible: Value and results matter more than initial price\n\nWhy this matters: We can focus on cost savings vs. premium features based on your flexibility.',
  },

  // STEP 7 - Interests & Engagement
  'treasury-optimization': {
    title: 'Treasury Optimization',
    content:
      'Maximizing returns on reserve funds through better investment options, optimized banking relationships, and strategic financial planning to grow community resources.',
  },
  'municipal-partnerships': {
    title: 'Municipal Partnerships',
    content:
      'Formal relationships with city/county government for shared services, infrastructure projects, or coordinated community development initiatives.',
  },
  'engagement-observer': {
    title: 'Observer Level Engagement',
    content:
      'You prefer to stay informed through newsletters, announcements, and occasional meetings, but rarely participate in discussions or volunteer for activities.',
  },
  'engagement-occasional': {
    title: 'Occasional Participant',
    content:
      'You participate selectively in community activities that align with your interests or expertise, attending meetings and events when topics are relevant to you.',
  },
  'engagement-regular': {
    title: 'Regular Participant',
    content:
      'You actively engage in community discussions, attend most meetings, participate in events, and contribute regularly to decision-making processes and community initiatives.',
  },
  'engagement-leader': {
    title: 'Community Leader Engagement',
    content:
      'You enjoy organizing events, leading committees, facilitating discussions, and taking initiative on community improvements and new programs.',
  },
  'additional-community-interests': {
    title: 'Why Additional Interests Matter',
    content:
      'Understanding your broader community interests helps us customize features, recommend relevant integrations, and connect you with like-minded communities in our network for shared best practices.',
  },
  'communication-preferences': {
    title: 'Communication Preferences',
    content:
      'This helps us set up your notification preferences and ensure you receive important updates through your preferred channels. You can always adjust these settings later in your account.',
  },
  'event-preferences': {
    title: 'Event & Activity Preferences',
    content:
      'Knowing your event interests helps us recommend relevant community features like group buying coordination, vendor fair management, and event planning tools that align with your community priorities.',
  },

  // Community Interest Tooltips
  'community-governance-interest': {
    title: 'Community Governance',
    content:
      'Participate in decision-making processes, board meetings, policy development, and voting on community matters that affect all residents.',
  },
  'financial-management-interest': {
    title: 'Financial Management',
    content:
      'Involvement in budgeting, reserve planning, fee structure decisions, cost-saving initiatives, and investment strategies for community funds.',
  },
  'event-planning-interest': {
    title: 'Event Planning',
    content:
      'Organize community gatherings, social events, educational workshops, seasonal celebrations, and activities that bring neighbors together.',
  },
  'maintenance-facilities-interest': {
    title: 'Maintenance & Facilities',
    content:
      'Oversight of property upkeep, facility maintenance, landscaping standards, common area improvements, and infrastructure projects.',
  },
  'communication-transparency-interest': {
    title: 'Communication & Transparency',
    content:
      'Improve information sharing, community newsletters, digital communication tools, meeting transparency, and resident engagement strategies.',
  },
  'vendor-management-interest': {
    title: 'Vendor Management',
    content:
      'Select and oversee service providers, negotiate contracts, manage vendor relationships, and ensure quality service delivery for the community.',
  },
  'resident-services-interest': {
    title: 'Resident Services',
    content:
      'Enhance services for community members including amenity management, complaint resolution, new resident orientation, and quality of life improvements.',
  },
  'community-safety-interest': {
    title: 'Community Safety',
    content:
      'Security measures, emergency preparedness, safety protocols, lighting improvements, and creating a secure environment for all residents.',
  },
  'environmental-initiatives-interest': {
    title: 'Environmental Initiatives',
    content:
      'Sustainability programs, energy efficiency projects, recycling initiatives, green landscaping, and environmentally conscious community improvements.',
  },
  'technology-innovation-interest': {
    title: 'Technology & Innovation',
    content:
      'Digital transformation, smart home integration, community apps, automated systems, and leveraging technology to improve community operations.',
  },

  // Event Preference Tooltips
  'educational-workshops': {
    title: 'Educational Workshops',
    content:
      'Learn about home maintenance, financial planning, legal rights, gardening, technology, and other topics relevant to homeowners and community living.',
  },
  'social-gatherings': {
    title: 'Social Gatherings',
    content:
      'Casual meetups, coffee hours, potluck dinners, game nights, book clubs, and other social activities to build neighborly connections.',
  },
  'group-purchases-bulk-buying': {
    title: 'Group Purchases/Bulk Buying',
    content:
      'Coordinate with neighbors to purchase items in bulk for better prices, such as landscaping supplies, home services, or seasonal decorations.',
  },
  'vendor-fairs-negotiations': {
    title: 'Vendor Fairs & Negotiations',
    content:
      'Meet potential service providers, compare options, negotiate better rates through group contracts, and evaluate vendors as a community.',
  },
  'financial-planning-workshops': {
    title: 'Financial Planning Workshops',
    content:
      'Learn about home equity, refinancing, insurance options, investment strategies, budgeting, and financial topics specific to homeowners.',
  },
  'digital-governance-training': {
    title: 'Digital Governance Training',
    content:
      'Learn to use community management platforms, participate in online voting, access digital documents, and engage with modern community tools.',
  },
  'fitness-wellness-activities': {
    title: 'Fitness & Wellness Activities',
    content:
      'Group exercise classes, walking clubs, yoga sessions, health screenings, wellness challenges, and activities promoting healthy living.',
  },
  'cultural-events': {
    title: 'Cultural Events',
    content:
      'Art shows, cultural celebrations, music performances, cultural exchange programs, and events celebrating the diversity of your community.',
  },
  'volunteer-opportunities': {
    title: 'Volunteer Opportunities',
    content:
      'Community service projects, neighborhood cleanup events, mentoring programs, helping elderly residents, and giving back to the local area.',
  },
  'business-networking': {
    title: 'Business Networking',
    content:
      'Connect with neighbor entrepreneurs, professional development opportunities, referral networks, and business collaboration within the community.',
  },
  'family-friendly-activities': {
    title: 'Family-Friendly Activities',
    content:
      'Events for children and families including playground activities, family movie nights, educational programs for kids, and intergenerational events.',
  },
  'seasonal-celebrations': {
    title: 'Seasonal Celebrations',
    content:
      'Holiday parties, seasonal festivals, summer barbecues, winter celebrations, spring cleanups, and events that mark special times of the year.',
  },

  // Primary Focus Area Tooltips - Progressive Disclosure
  'cost-reduction': {
    title: 'Group Buying Cost Savings',
    content:
      'Coordinate with neighbors to purchase services and supplies in bulk for better pricing. Includes landscaping, maintenance supplies, insurance, utilities, and seasonal decorations through collective purchasing power.',
  },
  'financial-management': {
    title: 'Financial Management & Treasury',
    content:
      'Comprehensive financial tools including budgeting, reserve fund optimization, investment strategies, automated bookkeeping, expense tracking, and financial reporting to maximize your community funds.',
  },
  'vendor-management': {
    title: 'Vendor Management & Coordination',
    content:
      'Streamline service provider relationships with centralized vendor databases, automated scheduling, performance tracking, contract management, and group rate negotiations for better service and pricing.',
  },
  'communication-transparency': {
    title: 'Communication & Transparency',
    content:
      'Enhanced community communication through digital messaging, announcement systems, meeting management, document sharing, and transparency tools that keep all residents informed and engaged.',
  },
  'maintenance-facilities': {
    title: 'Maintenance & Facilities Management',
    content:
      'Organized maintenance scheduling, work order management, facility booking systems, preventive maintenance tracking, and asset management to keep your community in excellent condition.',
  },
  'analytics-reporting': {
    title: 'Analytics & Advanced Reporting',
    content:
      'Data-driven insights with financial analytics, resident satisfaction metrics, maintenance trends, vendor performance reports, and predictive analytics to make informed community decisions.',
  },
  'multi-property': {
    title: 'Multi-Property Management',
    content:
      'Manage multiple communities or properties from a single dashboard with consolidated reporting, cross-property analytics, shared vendor networks, and economies of scale benefits.',
  },
  'investment-opportunities': {
    title: 'Community Investment & Wealth Building',
    content:
      'Strategic investment opportunities including treasury optimization, real estate development partnerships, community improvement financing, and wealth-building initiatives for long-term value creation.',
  },
  'governance-automation': {
    title: 'Automated Governance & Voting',
    content:
      'Digital democracy tools with secure online voting, automated meeting management, policy tracking, compliance monitoring, and governance workflows that streamline decision-making processes.',
  },
  'resident-services': {
    title: 'Enhanced Resident Services',
    content:
      'Premium resident experience through concierge services, amenity management, event coordination, complaint resolution systems, and quality-of-life improvements that increase property values.',
  },
  'custom-integrations': {
    title: 'Custom System Integrations',
    content:
      'Seamless integration with your existing software including accounting systems, property management platforms, banking interfaces, and custom databases through API connections and data synchronization.',
  },
  'infrastructure-projects': {
    title: 'Large Infrastructure & Municipal Bonds',
    content:
      'Enterprise-level project management for major infrastructure improvements, municipal bond financing, capital improvement planning, and large-scale construction project coordination.',
  },
  'municipal-partnerships-focus': {
    title: 'Municipal Government Partnerships',
    content:
      'Direct integration with city and county systems for permits, utilities, tax coordination, regulatory compliance, and collaborative community development with local government entities.',
  },
  'enterprise-scaling': {
    title: 'Enterprise-Scale Community Management',
    content:
      'Comprehensive management solutions for large communities, property management companies, or municipal entities with advanced workflows, multi-tier access controls, and scalable infrastructure.',
  },
  'regulatory-compliance': {
    title: 'Complex Regulatory & Compliance',
    content:
      'Advanced compliance management for complex regulatory environments including federal housing requirements, ADA compliance, financial regulations, and industry-specific legal requirements.',
  },

  // Governance Experience Options
  'governance-some-voting': {
    title: 'Some Voting Experience',
    content:
      'You have participated in community votes or elections, attended some meetings, and are familiar with basic democratic processes in community settings.',
  },
  'governance-active-decisions': {
    title: 'Active in Community Decisions',
    content:
      'You regularly attend meetings, participate in discussions, stay informed about issues, and actively contribute your opinions to community decision-making processes.',
  },
  'governance-led-initiatives': {
    title: 'Led Governance Initiatives',
    content:
      'You have experience leading committees, organizing meetings, driving policy changes, or taking leadership roles in community governance and decision-making.',
  },

  // STEP 8 - Package Recommendations
  'silver-package': {
    title: 'Silver Package Benefits',
    content:
      'Essential community management tools focused on cost savings, basic financial management, and streamlined operations. Best for communities wanting solid fundamentals at an affordable price.',
  },
  'gold-package': {
    title: 'Gold Package Benefits',
    content:
      'Advanced features including analytics, investment opportunities, multi-property management, and enhanced automation. Ideal for growing communities and management companies.',
  },
  'custom-package': {
    title: 'Custom Enterprise Package',
    content:
      'Fully customizable solution with API integrations, municipal partnerships, large-scale project management, and dedicated support. Designed for complex, enterprise-level needs.',
  },
};

// Helper function to get tooltip content
export const getTooltipContent = (key: string): TooltipContent | undefined => {
  return ONBOARDING_TOOLTIPS[key];
};
